import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { setProfile } from '../store/userSlice';
import { updateMedicalInfo } from '../services/profile';

const HEALTH_GOAL_OPTIONS = [
  { value: 'Energie', label: 'Energie' },
  { value: 'immuniteit', label: 'Immuniteit' },
  { value: 'slaap', label: 'Slaap' },
  { value: 'spijsvertering', label: 'Spijsvertering' },
  { value: 'stress', label: 'Stress' },
];

const ChevronDownSvg = ({ open = false }) => (
  <svg
    width="14"
    height="8"
    viewBox="0 0 14 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`transition-transform ${open ? 'rotate-180' : ''}`}
  >
    <path
      d="M8.81784 6.26613L12.7518 2.42682C12.9114 2.2701 13.001 2.0581 13.001 1.83712C13.001 1.61614 12.9114 1.40414 12.7518 1.24742C12.6721 1.16902 12.5773 1.10679 12.4728 1.06433C12.3684 1.02186 12.2564 1 12.1432 1C12.0301 1 11.9181 1.02186 11.8136 1.06433C11.7092 1.10679 11.6144 1.16902 11.5347 1.24742L7.60939 5.0951C7.52972 5.1735 7.43492 5.23572 7.33048 5.27819C7.22604 5.32066 7.11402 5.34252 7.00088 5.34252C6.88774 5.34252 6.77571 5.32066 6.67127 5.27819C6.56683 5.23572 6.47204 5.1735 6.39237 5.0951L2.46703 1.24742C2.30678 1.08991 2.08898 1.00099 1.86155 1.0002C1.63412 0.999418 1.41568 1.08684 1.25429 1.24324C1.09291 1.39964 1.00179 1.6122 1.00098 1.83416C1.00018 2.05613 1.08976 2.26931 1.25001 2.42682L5.18391 6.26613C5.66601 6.73605 6.31951 7 7.00088 7C7.68224 7 8.33575 6.73605 8.81784 6.26613Z"
      fill="#2D4343"
      stroke="#2D4343"
      strokeWidth="0.5"
    />
  </svg>
);

export default function MedicalInfoCard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [complaints, setComplaints] = useState('');
  const [healthGoals, setHealthGoals] = useState([]);
  const [supplementsAndDosage, setSupplementsAndDosage] = useState('');
  const [monthlySpend, setMonthlySpend] = useState('');
  const [sleepProblems, setSleepProblems] = useState('');
  const [sleepProblemsDetail, setSleepProblemsDetail] = useState('');
  const [dietVeganVegetarian, setDietVeganVegetarian] = useState('');
  const [takesMedication, setTakesMedication] = useState('');
  const [medicationDetail, setMedicationDetail] = useState('');
  const [hasAllergies, setHasAllergies] = useState('');
  const [allergiesDetail, setAllergiesDetail] = useState('');
  const [otherComplaintsOrRelevant, setOtherComplaintsOrRelevant] = useState('');
  const [sportRoutine, setSportRoutine] = useState('');
  const [bloodTestExpectations, setBloodTestExpectations] = useState('');
  const [serverErrors, setServerErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inputClass = 'w-full h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-4 outline-none font-poppins';
  const textareaClass = 'w-full min-h-[100px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] px-5 py-3 outline-none font-poppins resize-y';
  const labelClass = 'text-[#889696] font-poppins text-sm font-medium';
  const errorClass = 'text-red-500 text-sm font-poppins mt-1';
  const sectionClass = 'flex flex-col gap-2 w-full max-w-[540px]';
  const inputErrorBorder = 'border-red-400';

  const toggleHealthGoal = (value) => {
    setHealthGoals((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
    setFieldErrors((prev) => ({ ...prev, healthGoal: '' }));
  };

  const validate = () => {
    const err = {};
    if (!healthGoals?.length) err.healthGoal = 'Kies minimaal één gezondheidsdoel.';
    if (!sleepProblems) err.sleepProblems = 'Selecteer of je problemen met slapen hebt.';
    if (!dietVeganVegetarian) err.dietVeganVegetarian = 'Selecteer of je veganistisch/vegetarisch eet.';
    if (!takesMedication) err.takesMedication = 'Selecteer of je medicijnen slikt.';
    if (takesMedication === 'Ja' && !medicationDetail?.trim()) err.medicationDetail = 'Geef aan welke medicijnen.';
    if (!hasAllergies) err.hasAllergies = 'Selecteer of je allergieën hebt.';
    if (hasAllergies === 'Ja' && !allergiesDetail?.trim()) err.allergiesDetail = 'Geef aan welke allergieën.';
    if (!sportRoutine?.trim()) err.sportRoutine = 'Omschrijf je sport routine.';
    if (!bloodTestExpectations?.trim()) err.bloodTestExpectations = 'Geef aan wat je uit de bloedtest hoopt te halen.';
    setFieldErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setServerErrors({});
    setFieldErrors({});
    if (!validate()) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setIsSubmitting(true);

    const payload = {
      complaints: complaints.trim() || undefined,
      health_goal: healthGoals.length ? healthGoals.join(', ') : undefined,
      supplements_and_dosage: supplementsAndDosage.trim() || undefined,
      monthly_supplement_spend: monthlySpend.trim() || undefined,
      sleep_problems: sleepProblems === 'Ja' ? true : sleepProblems === 'Nee' ? false : undefined,
      sleep_problems_detail: sleepProblems === 'Ja' ? sleepProblemsDetail.trim() || undefined : undefined,
      diet_vegetarian_vegan: dietVeganVegetarian || undefined,
      takes_medication: takesMedication === 'Ja' ? true : takesMedication === 'Nee' ? false : undefined,
      medication_detail: takesMedication === 'Ja' ? medicationDetail.trim() || undefined : undefined,
      has_allergies: hasAllergies === 'Ja' ? true : hasAllergies === 'Nee' ? false : undefined,
      allergies_detail: hasAllergies === 'Ja' ? allergiesDetail.trim() || undefined : undefined,
      other_complaints_or_relevant: otherComplaintsOrRelevant.trim() || undefined,
      sport_routine: sportRoutine.trim() || undefined,
      blood_test_expectations: bloodTestExpectations.trim() || undefined,
    };

    try {
      const data = await updateMedicalInfo(payload);
      const newStep = data?.on_boarding_step != null ? String(data.on_boarding_step) : '';
      const newCompleted = newStep === '' ? true : false;

      dispatch(
        setProfile({
          on_boarding_completed: newCompleted,
          on_boarding_step: newStep,
        })
      );
      try {
        localStorage.setItem('on_boarding_completed', String(newCompleted));
        localStorage.setItem('on_boarding_step', newStep);
      } catch (_) {
        // ignore localStorage errors
      }

      toast.success('Medical information saved.');
      const userRole = localStorage.getItem('userRole') || 'Patient';
      // Temporarily: do not redirect to /medical-info when step is Medical Info
      // const redirectPath =
      //   newStep === 'Medical Info'
      //     ? '/medical-info'
      //     : userRole === 'Doctor/Nurse'
      //     ? '/prescription'
      //     : '/profile';
      const redirectPath =
        userRole === 'Doctor/Nurse' ? '/prescription' : '/profile';
      navigate(redirectPath);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        err?.message ||
        'Could not save medical information.';
      setServerErrors({ submit: msg });
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 shadow-sm w-full max-w-[660px] p-5 md:p-[60px] overflow-hidden"
      style={{ position: 'relative', zIndex: 1 }}
    >
      <div className="flex flex-col items-center gap-3 w-full">
        <h1 className="text-[#2B3133] font-libreBaskerville text-3xl md:text-[40px] leading-[1em] text-center">
          Medical information
        </h1>
        <p className="text-[#889696] font-poppins text-sm md:text-base text-center">
          Please answer the questions below as fully as possible.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-6 mt-10">
        <div className="flex flex-col items-center gap-6 w-full">
          {/* 1. Complaints */}
          <div className={sectionClass}>
            <label className={labelClass}>
              1. Heb je bepaalde klachten op dit moment? (denk aan slaap, stress, energie, focus etc.)
            </label>
            <textarea
              value={complaints}
              onChange={(e) => setComplaints(e.target.value)}
              className={textareaClass}
              placeholder="Bijv. vermoeidheid, slecht slapen..."
              rows={3}
            />
          </div>

          {/* 2. Health goal - multi-select */}
          <div className={sectionClass}>
            <label className={labelClass}>
              2. Welk van deze gezondheidsdoelen wil je het liefst behalen? <span className="text-red-500">*</span>
            </label>
            <div className={`flex flex-wrap gap-3 p-4 rounded-xl border ${fieldErrors.healthGoal ? 'border-red-400' : 'border-[#CBD5E0]'} bg-[#F8F8F8]`}>
              {HEALTH_GOAL_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 cursor-pointer font-poppins text-sm text-[#2B3133]"
                >
                  <input
                    type="checkbox"
                    checked={healthGoals.includes(opt.value)}
                    onChange={() => toggleHealthGoal(opt.value)}
                    className="w-4 h-4 rounded border-[#CBD5E0] text-[#119975] focus:ring-[#119975]"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            {fieldErrors.healthGoal && <p className={errorClass}>{fieldErrors.healthGoal}</p>}
          </div>

          {/* 3. Supplements */}
          <div className={sectionClass}>
            <label className={labelClass}>
              3. Welke supplementen slik je en in welke dagdosering?
            </label>
            <textarea
              value={supplementsAndDosage}
              onChange={(e) => setSupplementsAndDosage(e.target.value)}
              className={textareaClass}
              placeholder="Bijv. vitamine D 25 mcg per dag..."
              rows={3}
            />
          </div>

          {/* 4. Monthly spend */}
          <div className={sectionClass}>
            <label className={labelClass}>
              4. Hoeveel geef je gemiddeld ongeveer per maand uit aan deze supplementen?
            </label>
            <input
              type="text"
              value={monthlySpend}
              onChange={(e) => setMonthlySpend(e.target.value)}
              className={inputClass}
              placeholder="Bijv. € 30 per maand"
            />
          </div>

          {/* 5. Sleep problems */}
          <div className={sectionClass}>
            <label className={labelClass}>
              5. Heb je problemen met in- en/of doorslapen? <span className="text-red-500">*</span>
            </label>
            <div className={`relative h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] ${fieldErrors.sleepProblems ? inputErrorBorder : ''}`}>
              <select
                value={sleepProblems}
                onChange={(e) => { setSleepProblems(e.target.value); setFieldErrors((prev) => ({ ...prev, sleepProblems: '' })); }}
                className={`w-full h-full bg-transparent px-4 pr-12 outline-none appearance-none cursor-pointer font-poppins text-left text-sm ${sleepProblems ? 'text-[#2B3133]' : 'text-gray-400'}`}
              >
                <option value="">Kies</option>
                <option value="Nee">Nee</option>
                <option value="Ja">Ja</option>
              </select>
              <span className="pointer-events-none absolute right-9 top-0 h-full w-px bg-[#CBD5E0]" />
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                <ChevronDownSvg />
              </div>
            </div>
            {fieldErrors.sleepProblems && <p className={errorClass}>{fieldErrors.sleepProblems}</p>}
            {sleepProblems === 'Ja' && (
              <textarea
                value={sleepProblemsDetail}
                onChange={(e) => setSleepProblemsDetail(e.target.value)}
                className={textareaClass}
                placeholder="Omschrijf kort..."
                rows={2}
              />
            )}
          </div>

          {/* 6. Vegan/vegetarian - single option Vegetarisch/Veganistisch */}
          <div className={sectionClass}>
            <label className={labelClass}>
              6. Eet jij veganistisch/vegetarisch? <span className="text-red-500">*</span>
            </label>
            <div className={`relative h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] ${fieldErrors.dietVeganVegetarian ? inputErrorBorder : ''}`}>
              <select
                value={dietVeganVegetarian}
                onChange={(e) => { setDietVeganVegetarian(e.target.value); setFieldErrors((prev) => ({ ...prev, dietVeganVegetarian: '' })); }}
                className={`w-full h-full bg-transparent px-4 pr-12 outline-none appearance-none cursor-pointer font-poppins text-left text-sm ${dietVeganVegetarian ? 'text-[#2B3133]' : 'text-gray-400'}`}
              >
                <option value="">Kies</option>
                <option value="Nee">Nee</option>
                <option value="Vegetarisch/Veganistisch">Vegetarisch/Veganistisch</option>
              </select>
              <span className="pointer-events-none absolute right-9 top-0 h-full w-px bg-[#CBD5E0]" />
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                <ChevronDownSvg />
              </div>
            </div>
            {fieldErrors.dietVeganVegetarian && <p className={errorClass}>{fieldErrors.dietVeganVegetarian}</p>}
          </div>

          {/* 7. Medication */}
          <div className={sectionClass}>
            <label className={labelClass}>
              7. Slik je medicijnen? Zo ja, welke? <span className="text-red-500">*</span>
            </label>
            <div className={`relative h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] ${fieldErrors.takesMedication ? inputErrorBorder : ''}`}>
              <select
                value={takesMedication}
                onChange={(e) => { setTakesMedication(e.target.value); setFieldErrors((prev) => ({ ...prev, takesMedication: '', medicationDetail: '' })); }}
                className={`w-full h-full bg-transparent px-4 pr-12 outline-none appearance-none cursor-pointer font-poppins text-left text-sm ${takesMedication ? 'text-[#2B3133]' : 'text-gray-400'}`}
              >
                <option value="">Kies</option>
                <option value="Nee">Nee</option>
                <option value="Ja">Ja</option>
              </select>
              <span className="pointer-events-none absolute right-9 top-0 h-full w-px bg-[#CBD5E0]" />
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                <ChevronDownSvg />
              </div>
            </div>
            {fieldErrors.takesMedication && <p className={errorClass}>{fieldErrors.takesMedication}</p>}
            {takesMedication === 'Ja' && (
              <>
                <textarea
                  value={medicationDetail}
                  onChange={(e) => { setMedicationDetail(e.target.value); setFieldErrors((prev) => ({ ...prev, medicationDetail: '' })); }}
                  className={`${textareaClass} ${fieldErrors.medicationDetail ? inputErrorBorder : ''}`}
                  placeholder="Welke medicijnen?"
                  rows={2}
                />
                {fieldErrors.medicationDetail && <p className={errorClass}>{fieldErrors.medicationDetail}</p>}
              </>
            )}
          </div>

          {/* 8. Allergies */}
          <div className={sectionClass}>
            <label className={labelClass}>
              8. Heb je allergieën? Zo ja, welke? <span className="text-red-500">*</span>
            </label>
            <div className={`relative h-[52px] rounded-xl border border-[#CBD5E0] bg-[#F8F8F8] ${fieldErrors.hasAllergies ? inputErrorBorder : ''}`}>
              <select
                value={hasAllergies}
                onChange={(e) => { setHasAllergies(e.target.value); setFieldErrors((prev) => ({ ...prev, hasAllergies: '', allergiesDetail: '' })); }}
                className={`w-full h-full bg-transparent px-4 pr-12 outline-none appearance-none cursor-pointer font-poppins text-left text-sm ${hasAllergies ? 'text-[#2B3133]' : 'text-gray-400'}`}
              >
                <option value="">Kies</option>
                <option value="Nee">Nee</option>
                <option value="Ja">Ja</option>
              </select>
              <span className="pointer-events-none absolute right-9 top-0 h-full w-px bg-[#CBD5E0]" />
              <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
                <ChevronDownSvg />
              </div>
            </div>
            {fieldErrors.hasAllergies && <p className={errorClass}>{fieldErrors.hasAllergies}</p>}
            {hasAllergies === 'Ja' && (
              <>
                <textarea
                  value={allergiesDetail}
                  onChange={(e) => { setAllergiesDetail(e.target.value); setFieldErrors((prev) => ({ ...prev, allergiesDetail: '' })); }}
                  className={`${textareaClass} ${fieldErrors.allergiesDetail ? inputErrorBorder : ''}`}
                  placeholder="Welke allergieën?"
                  rows={2}
                />
                {fieldErrors.allergiesDetail && <p className={errorClass}>{fieldErrors.allergiesDetail}</p>}
              </>
            )}
          </div>

          {/* 9. Other complaints */}
          <div className={sectionClass}>
            <label className={labelClass}>
              9. Heb je nog andere klachten of zijn er relevante dingen die je met ons wil delen?
            </label>
            <textarea
              value={otherComplaintsOrRelevant}
              onChange={(e) => setOtherComplaintsOrRelevant(e.target.value)}
              className={textareaClass}
              placeholder="Optioneel"
              rows={3}
            />
          </div>

          {/* 10. Sport routine */}
          <div className={sectionClass}>
            <label className={labelClass}>
              10. Hoe omschrijf je je sport routine? <span className="text-red-500">*</span>
            </label>
            <textarea
              value={sportRoutine}
              onChange={(e) => { setSportRoutine(e.target.value); setFieldErrors((prev) => ({ ...prev, sportRoutine: '' })); }}
              className={`${textareaClass} ${fieldErrors.sportRoutine ? inputErrorBorder : ''}`}
              placeholder="Bijv. 3x per week hardlopen..."
              rows={3}
            />
            {fieldErrors.sportRoutine && <p className={errorClass}>{fieldErrors.sportRoutine}</p>}
          </div>

          {/* 11. Blood test expectations */}
          <div className={sectionClass}>
            <label className={labelClass}>
              11. Wat hoop je uit deze bloedtest te halen? <span className="text-red-500">*</span>
            </label>
            <textarea
              value={bloodTestExpectations}
              onChange={(e) => { setBloodTestExpectations(e.target.value); setFieldErrors((prev) => ({ ...prev, bloodTestExpectations: '' })); }}
              className={`${textareaClass} ${fieldErrors.bloodTestExpectations ? inputErrorBorder : ''}`}
              placeholder="Bijv. inzicht in mijn vitaminewaarden..."
              rows={3}
            />
            {fieldErrors.bloodTestExpectations && <p className={errorClass}>{fieldErrors.bloodTestExpectations}</p>}
          </div>

          {serverErrors.submit && (
            <p className="text-red-500 text-sm font-poppins w-full max-w-[540px]">{serverErrors.submit}</p>
          )}

          <div className="w-full max-w-[540px] pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full h-[60px] rounded-xl bg-[#119975] text-white font-poppins text-base font-medium hover:opacity-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
